import React from 'react';
import { Delete } from '@mui/icons-material';
import { Box, Button, IconButton, List, ListItem, Typography } from '@mui/material';

import { formatLumberGrade, formatLumberSpecies, formatLumberSpeciesLong, LumberSet } from '@paragon/contract-library';

import { LumberGradeSelect } from '../controls/lumber/LumberGradeSelect';
import { LumberLengthsField } from '../controls/lumber/LumberLengthsField';
import { LumberSpeciesSelect } from '../controls/lumber/LumberSpeciesSelect';
import { LumberTreatmentTypesSelect } from '../controls/lumber/LumberTreatmentTypesSelect';
import { StockLengthsField } from '../controls/lumber/StockLengthsField';

interface Props {
  readonly value: LumberSet;
  onChange(changes: Partial<LumberSet>): void;
}

export function LumberSetForm({ value, onChange }: Props) {
  return (
    <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <LumberSpeciesSelect
          value={value.species}
          onChange={(species) => {
            onChange({ species });
          }}
        />
        <Button
          variant="outlined"
          onClick={() => onChange({ species: [], comboSpecies: [...value.comboSpecies, value.species] })}
          disabled={
            value.species.length < 2 ||
            !!value.comboSpecies.find(
              (comboSpecies) =>
                formatLumberSpecies('Combo', comboSpecies) === formatLumberSpecies('Combo', value.species),
            )
          }
        >
          Add as Combo Species
        </Button>
      </Box>

      {value.comboSpecies.length > 0 && (
        <>
          <Typography sx={{ ml: 2 }}>Combo Species:</Typography>
          <List>
            {value.comboSpecies.map((comboSpecies) => (
              <ListItem
                key={formatLumberSpeciesLong('Combo', comboSpecies)}
                secondaryAction={
                  <IconButton
                    onClick={() => onChange({ comboSpecies: value.comboSpecies.filter((cs) => cs !== comboSpecies) })}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                {formatLumberSpeciesLong('Combo', comboSpecies)}
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <LumberGradeSelect
          value={value.grades}
          onChange={(grades) => {
            onChange({ grades });
          }}
          disabled={!value.species.length && !value.comboSpecies.length}
          selectedSpecies={value.species}
        />
        <Button
          variant="outlined"
          onClick={() => onChange({ grades: [], comboGrades: [...value.comboGrades, value.grades] })}
          disabled={
            value.grades.length < 2 ||
            !!value.comboGrades.find(
              (comboGrades) => formatLumberGrade('Combo', comboGrades) === formatLumberGrade('Combo', value.grades),
            )
          }
        >
          Add as Combo Grade
        </Button>
      </Box>

      {value.comboGrades.length > 0 && (
        <>
          <Typography sx={{ ml: 2 }}>Combo Grades:</Typography>
          <List>
            {value.comboGrades.map((comboGrades) => (
              <ListItem
                key={formatLumberGrade('Combo', comboGrades)}
                secondaryAction={
                  <IconButton
                    onClick={() =>
                      onChange({ comboGrades: value.comboGrades.filter((comboGrade) => comboGrade !== comboGrades) })
                    }
                  >
                    <Delete />
                  </IconButton>
                }
              >
                {formatLumberGrade('Combo', comboGrades)}
              </ListItem>
            ))}
          </List>
        </>
      )}

      <LumberTreatmentTypesSelect
        value={value.treatmentTypes}
        onChange={(treatmentTypes) => onChange({ treatmentTypes })}
      />
      <Box sx={{ display: 'flex' }}>
        <LumberLengthsField
          sx={{ m: 1, flex: 1 }}
          label="Widths"
          placeholder={`Example: 1.5", 3.5"`}
          tooltip="Actual Dimensions"
          values={value.actualDimensions1}
          onChange={(actualDimensions1) => {
            onChange({ actualDimensions1 });
          }}
        />

        <LumberLengthsField
          sx={{ m: 1, flex: 1 }}
          label="Heights"
          placeholder={`Example: 3.5", 5.5"`}
          tooltip="Actual Dimensions"
          values={value.actualDimensions2}
          onChange={(actualDimensions2) => {
            onChange({ actualDimensions2 });
          }}
        />
      </Box>
      <StockLengthsField
        value={value.stockLengths}
        onChange={(stockLengths) => {
          onChange({ stockLengths });
        }}
      />
    </Box>
  );
}
